import { useState, useRef } from 'react';
import { X, Check, Upload, RefreshCw, Loader2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const COVER_REGENERATION_LIMIT = 2;

const SeriesCoverPicker = ({
  series,
  isOpen,
  onClose,
  onCoverChange,
}) => {
  // Initialize state from series data
  const initCovers = () => {
    if (series.cover_images && series.cover_images.length > 0) {
      return series.cover_images;
    }
    return series.cover_image_url ? [series.cover_image_url] : [];
  };

  const [state, setState] = useState('selecting'); // 'selecting' | 'regenerating' | 'uploading'
  const [covers, setCovers] = useState(initCovers);
  const [activeIndex, setActiveIndex] = useState(series.active_cover_index || 0);
  const [regenerationCount, setRegenerationCount] = useState(series.cover_regeneration_count || 0);
  const fileInputRef = useRef(null);

  const regenerationsRemaining = COVER_REGENERATION_LIMIT - regenerationCount;
  const canRegenerate = regenerationsRemaining > 0;

  const handleSelectCover = async (index) => {
    if (index === activeIndex) return;

    try {
      const response = await axios.post(
        `${API}/v1/series/${series.id}/cover/set`,
        { cover_index: index },
        { withCredentials: true }
      );

      setActiveIndex(index);
      onCoverChange(response.data.cover_url, response.data.cover_images, index);
      toast.success('Cover updated');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update cover';
      toast.error(message);
    }
  };

  const handleRegenerate = async () => {
    if (!canRegenerate) return;

    setState('regenerating');

    try {
      const response = await axios.post(
        `${API}/v1/series/${series.id}/cover/generate`,
        {},
        { withCredentials: true }
      );

      const newCovers = response.data.cover_images;
      const newActiveIndex = response.data.active_cover_index;

      setCovers(newCovers);
      setActiveIndex(newActiveIndex);
      setRegenerationCount(response.data.cover_regeneration_count);
      onCoverChange(response.data.cover_url, newCovers, newActiveIndex);
      toast.success('New cover generated');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate cover';
      toast.error(message);
    } finally {
      setState('selecting');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPG, PNG, or WebP.');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setState('uploading');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/v1/series/${series.id}/cover/upload`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      const newCovers = response.data.cover_images;
      const newActiveIndex = response.data.active_cover_index;

      setCovers(newCovers);
      setActiveIndex(newActiveIndex);
      onCoverChange(response.data.cover_url, newCovers, newActiveIndex);
      toast.success('Cover uploaded');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload cover';
      toast.error(message);
    } finally {
      setState('selecting');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClose = () => {
    if (state !== 'selecting') return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.5)' }}
      onClick={handleClose}
    >
      <div
        className="rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          background: 'var(--st-bg-surface)',
          border: '1px solid var(--st-border-light)',
          boxShadow: '0 12px 32px rgba(var(--st-shadow-color), 0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'var(--st-border-light)' }}
        >
          <div className="flex items-center gap-3">
            <ImageIcon className="w-5 h-5" style={{ color: '#818CF8' }} />
            <h2
              className="text-lg font-bold"
              style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
            >
              Series Cover
            </h2>
            <span
              className={`text-sm px-2 py-0.5 rounded-full ${canRegenerate ? 'dark:bg-green-900/40' : 'dark:bg-red-900/40'}`}
              style={{
                fontFamily: 'Quicksand, sans-serif',
                color: canRegenerate ? '#16A34A' : '#DC2626',
                background: canRegenerate ? '#DCFCE7' : '#FEE2E2',
              }}
            >
              {regenerationsRemaining} regeneration{regenerationsRemaining !== 1 ? 's' : ''} left
            </span>
          </div>
          <button
            onClick={handleClose}
            disabled={state !== 'selecting'}
            className="p-2 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {state === 'regenerating' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2
                className="w-12 h-12 animate-spin"
                style={{ color: '#818CF8' }}
              />
              <p
                className="text-lg font-semibold"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
              >
                Generating new cover...
              </p>
              <p
                className="text-sm"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                This may take a moment
              </p>
            </div>
          )}

          {state === 'uploading' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2
                className="w-12 h-12 animate-spin"
                style={{ color: '#818CF8' }}
              />
              <p
                className="text-lg font-semibold"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
              >
                Uploading cover...
              </p>
            </div>
          )}

          {state === 'selecting' && (
            <div className="space-y-4">
              {/* Cover Grid */}
              <div className="grid grid-cols-3 gap-4">
                {covers.map((cover, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelectCover(index)}
                    className="relative aspect-[4/5] rounded-xl overflow-hidden transition-all hover:scale-[1.02]"
                    style={{
                      border: index === activeIndex
                        ? '3px solid #818CF8'
                        : '1px solid var(--st-border-light)',
                      boxShadow: index === activeIndex
                        ? '0 6px 20px rgba(129, 140, 248, 0.4)'
                        : '0 2px 8px rgba(var(--st-shadow-color), 0.08)',
                    }}
                  >
                    <img
                      src={getImageUrl(cover) || ''}
                      alt={`Cover ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {index === activeIndex && (
                      <div
                        className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ background: '#818CF8' }}
                      >
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div
                      className="absolute bottom-0 left-0 right-0 py-1 px-2 text-xs text-center"
                      style={{
                        background: 'var(--st-bg-surface)',
                        fontFamily: 'Quicksand, sans-serif',
                        color: 'var(--st-text-heading)',
                      }}
                    >
                      {index === 0 ? 'Original' : `Version ${index + 1}`}
                    </div>
                  </button>
                ))}

                {/* Add more placeholder if less than 3 covers and can regenerate */}
                {covers.length < 3 && canRegenerate && (
                  <button
                    onClick={handleRegenerate}
                    className="aspect-[4/5] rounded-xl flex flex-col items-center justify-center gap-2 transition-all hover:scale-[1.02] series-generate-btn"
                  >
                    <RefreshCw className="w-6 h-6" style={{ color: '#818CF8' }} />
                    <span
                      className="text-xs font-semibold"
                      style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                    >
                      Generate New
                    </span>
                  </button>
                )}

                {/* Upload custom placeholder */}
                <button
                  onClick={handleUploadClick}
                  className="aspect-[4/5] rounded-xl flex flex-col items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                  style={{
                    border: '2px dashed var(--st-border-light)',
                    background: 'var(--st-bg-muted)',
                  }}
                >
                  <Upload className="w-6 h-6" style={{ color: 'var(--st-text-muted)' }} />
                  <span
                    className="text-xs font-semibold"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                  >
                    Upload Custom
                  </span>
                </button>
              </div>

              {/* Help text */}
              <p
                className="text-xs text-center"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                Click a cover to set it as your series cover, or generate/upload a new one
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-6 py-4 border-t"
          style={{ borderColor: 'var(--st-border-light)' }}
        >
          <span
            className="text-xs"
            style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
          >
            {covers.length} cover{covers.length !== 1 ? 's' : ''} saved
          </span>
          <div className="flex items-center gap-3">
            {canRegenerate && covers.length >= 3 && (
              <button
                onClick={handleRegenerate}
                disabled={state !== 'selecting'}
                className="px-4 py-2.5 rounded-xl font-semibold text-sm transition-all hover:bg-indigo-50 dark:hover:bg-indigo-900/30 disabled:opacity-50"
                style={{
                  background: 'var(--st-bg-surface)',
                  border: '1px solid var(--st-border-light)',
                  color: 'var(--st-text-heading)',
                  fontFamily: 'Quicksand, sans-serif',
                }}
              >
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Generate New
                </span>
              </button>
            )}
            <button
              onClick={handleClose}
              disabled={state !== 'selecting'}
              className="px-6 py-2.5 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)',
                color: '#FFF8F0',
                fontFamily: 'Quicksand, sans-serif',
                boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
              }}
            >
              Done
            </button>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    </div>
  );
};

export default SeriesCoverPicker;
